import React from 'react';
import { useState,  useRef,useEffect } from 'react';
import './classes/CourseOverview.css';
import './classes/marquee.css';
import './classes/ConferenceRegistration.css';
import BeTheFirstToKnowPopup from './BeTheFirstToKnowPopup';
import DocTalkPopup from './DocTalkPopup';
import CurrencyConverter from "./shortcodes/CurrencyConverter";
import CourseCalendar from "./shortcodes/CourseCalendar";
import SidebarQuickForm from './SidebarQuickForm';
import Mentor from './Mentor';
import { createRoot } from "react-dom/client";

import { configData } from "../config";
import drupalAutop from "./drupalAutop";

// Pages whose text format on the live site has Drupal's "Convert line breaks into HTML" filter:
// there a double <br /> in a paragraph starts a new paragraph (drupalAutop). The format is not
// copied to this site, so the pages are listed by node id: the Master's of Professional Studies
// degree page (274987).
const LINE_BREAK_PAGES = new Set([274987]);

// Audio players in page bodies, by the name of the Drupal script that sets each one up
// (/sites/all/themes/smww/js/audio/<name>): the picture and recording it plays.
const AUDIO_PLAYERS = {
    'mel-kipper.js': { image: '/audio/mel_kiper.png', audio: '/audio/MelKiperJrAudio.mp3' },
};

const Body = ({ page }) => {
  const app_url = configData.APP_URL;
 const bodyRef = useRef(null);
 const [showBeTheFirstToKnow, setShowBeTheFirstToKnow] = useState(false);
 // The DOC Talks form that is open: { mode: 'register' | 'recording', id, mentors }
 const [docTalkPopup, setDocTalkPopup] = useState(null);
useEffect(() => {
    const ul = document.getElementById("marquee-horizontal");

    if (ul && !ul.dataset.initialized) {
        ul.innerHTML += ul.innerHTML;
        ul.dataset.initialized = "true";
    }
}, [page?.body]);
useEffect(() => {
    if (!bodyRef.current) return;

    bodyRef.current
        .querySelectorAll('[data-type="currency-converter"]')
        .forEach((element) => {
            // Mount it once: a second converter on the same element would add a second price line
            if (element.dataset.initialized) return;
            element.dataset.initialized = "true";

            createRoot(element).render(
                <CurrencyConverter
                    price={parseFloat(element.dataset.price)}
                />
            );
        });

         bodyRef.current
        .querySelectorAll('[data-type="mobile-quick-form"]')
        .forEach((element) => {
            createRoot(element).render(
                <div className="mobile-only">
                    <SidebarQuickForm page={page} />
                </div>
            );
        });
        bodyRef.current
        .querySelectorAll('[data-type="course-calendar"]')
        .forEach((element) => {
            createRoot(element).render(<CourseCalendar />);
        });
        bodyRef.current
        .querySelectorAll('[data-type="mobile-mentor-block"]')
        .forEach((element) => {
            // Shown under 631px, as the live theme's .mobile_mentor_block (Mentor.css)
            createRoot(element).render(
                <div className="mobile-mentor-block">
                    <Mentor page={page} />
                </div>
            );
        });

}, [page?.body]);
// Home page video: on the Drupal site a script swaps the cover image for the
// video when it is clicked. Scripts in the body are not run here, so do the same.
useEffect(() => {
    if (!bodyRef.current) return;

    const overlays = bodyRef.current.querySelectorAll('.video_overlay');
    const playVideo = (event) => {
        const overlay = event.currentTarget;
        const cover = overlay.querySelector('.cover_image');
        if (!cover) return;

        const video = document.createElement('video');
        // The video sits next to its cover image in the Drupal files folder
        video.src = new URL('home_page_video1.mp4', cover.src).href;
        video.controls = true;
        video.autoplay = true;
        overlay.parentNode.replaceChild(video, overlay);
    };

    overlays.forEach((overlay) => overlay.addEventListener('click', playVideo));
    return () => overlays.forEach((overlay) => overlay.removeEventListener('click', playVideo));
}, [page?.body]);
// YouTube players (<div class="video" data-id="...">): the Drupal site's script puts the
// video's cover picture and a play button in them, and swaps in the video when clicked.
useEffect(() => {
    if (!bodyRef.current) return;

    bodyRef.current.querySelectorAll('.player .video[data-id]').forEach((video) => {
        if (video.querySelector('div, iframe')) return;

        const cover = document.createElement('div');
        const thumb = document.createElement('img');
        thumb.src = 'https://i.ytimg.com/vi/' + video.dataset.id + '/hqdefault.jpg';
        thumb.alt = '';
        cover.append(thumb, document.createElement('span'));
        cover.onclick = () => {
            const iframe = document.createElement('iframe');
            iframe.src = 'https://www.youtube.com/embed/' + video.dataset.id + '?autoplay=1';
            iframe.allowFullscreen = true;
            iframe.allow = 'autoplay';
            iframe.frameBorder = '0';
            video.replaceChild(iframe, cover);
        };
        video.appendChild(cover);
    });
}, [page?.body]);
// Audio players (<div class="audio-player-wrapper">): on the Drupal site each one is set up by
// its own script in the body, which names the picture and the recording. Scripts in the body
// are not run here, so the same is done for the scripts listed in AUDIO_PLAYERS.
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const wrapper = body.querySelector('.audio-player-wrapper');
    const script = [...body.querySelectorAll('script[src*="/js/audio/"]')]
        .map((el) => el.getAttribute('src').split('/').pop())
        .find((name) => AUDIO_PLAYERS[name]);
    if (!wrapper || !script || wrapper.querySelector('audio')) return;

    const song = AUDIO_PLAYERS[script];
    const audio = document.createElement('audio');
    audio.src = configData.DRUPAL_DOMAIN_URL + song.audio;
    audio.preload = 'none';
    const image = document.createElement('img');
    image.src = configData.DRUPAL_DOMAIN_URL + song.image;
    image.alt = '';
    wrapper.querySelector('.audio-player-image')?.appendChild(image);
    wrapper.appendChild(audio);

    const button = wrapper.querySelector('.audio-player-button');
    const progress = wrapper.querySelector('.audio-player-progress');
    const bar = wrapper.querySelector('.audio-player-progress-bar');
    const showState = () => {
        button?.classList.toggle('icon-play', audio.paused);
        button?.classList.toggle('icon-pause', !audio.paused);
    };
    const showProgress = () => {
        if (bar && audio.duration) bar.style.width = (audio.currentTime / audio.duration) * 100 + '%';
    };
    const togglePlay = () => (audio.paused ? audio.play() : audio.pause());
    const seek = (event) => {
        if (audio.duration) audio.currentTime = audio.duration * (event.offsetX / progress.offsetWidth);
    };

    audio.addEventListener('play', showState);
    audio.addEventListener('pause', showState);
    audio.addEventListener('timeupdate', showProgress);
    button?.addEventListener('click', togglePlay);
    progress?.addEventListener('click', seek);
    return () => {
        audio.pause();
        button?.removeEventListener('click', togglePlay);
        progress?.removeEventListener('click', seek);
        image.remove();
        audio.remove();
    };
}, [page?.body]);
// Conference pages: the "Be The First To Know" buttons in the body open the sign-up popup
useEffect(() => {
    if (!bodyRef.current) return;

    const buttons = bodyRef.current.querySelectorAll('[data-type="be-the-first-to-know"]');
    const openPopup = (event) => {
        event.preventDefault();
        setShowBeTheFirstToKnow(true);
    };

    buttons.forEach((button) => button.addEventListener('click', openPopup));
    return () => buttons.forEach((button) => button.removeEventListener('click', openPopup));
}, [page?.body]);
// Collapsible sections (<div class="dp-accordion"><p class="morebar" data-target="#id">): the
// page's own inline script opened and closed them on the Drupal site (jQuery slideToggle); the
// page's own styles hide the sections and turn the arrow on ".open".
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    let slides = 0;
    const toggle = (event) => {
        const bar = event.target.closest('.dp-accordion > .morebar');
        if (!bar) return;
        event.preventDefault();
        const section = bar.dataset.target && body.querySelector(bar.dataset.target);
        const opening = bar.classList.toggle('open');
        if (!section) return;

        // A click during a slide takes over from it
        const slide = String(++slides);
        section.dataset.slide = slide;
        section.getAnimations().forEach((animation) => animation.cancel());
        const done = () => {
            if (section.dataset.slide !== slide) return;
            section.getAnimations().forEach((animation) => animation.cancel());
            section.style.display = opening ? 'block' : 'none';
            section.style.overflow = '';
        };
        section.style.display = 'block';
        section.style.overflow = 'hidden';
        const height = section.scrollHeight + 'px';
        section.animate({ height: opening ? ['0px', height] : [height, '0px'] }, { duration: 400, easing: 'ease' }).onfinish = done;
        // A tab in the background does not run animations: the section still ends up open or closed
        setTimeout(done, 450);
    };

    body.addEventListener('click', toggle);
    return () => body.removeEventListener('click', toggle);
}, [page?.body]);
// DOC Talks page: the year tabs show the past talks of one year (the current one at first), and
// the talk cards' buttons open the registration and recording forms. Drupal's doctalk.js did this.
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const earlier = body.querySelector('#earlier-years');
    const showYear = (year) => {
        body.querySelectorAll('.past-talk-card').forEach((talk) => {
            talk.style.display = year === 'all' || talk.dataset.year === year ? '' : 'none';
        });
    };
    const activeTab = body.querySelector('.year-tab.active');
    if (activeTab) showYear(activeTab.dataset.year);

    const onClick = (event) => {
        const tab = event.target.closest('.year-tab');
        const register = event.target.closest('[data-doc-talk-register]');
        const recording = event.target.closest('[data-doc-talk-recording]');
        if (tab) {
            body.querySelectorAll('.year-tab').forEach((other) => other.classList.remove('active'));
            tab.classList.add('active');
            showYear(tab.dataset.year);
            if (earlier) earlier.value = '';
        } else if (register) {
            event.preventDefault();
            setDocTalkPopup({ mode: 'register', id: register.dataset.docTalkRegister });
        } else if (recording) {
            event.preventDefault();
            setDocTalkPopup({ mode: 'recording', id: recording.dataset.docTalkRecording, mentors: recording.dataset.mentors });
        }
    };
    const onEarlier = () => {
        body.querySelectorAll('.year-tab').forEach((tab) => tab.classList.remove('active'));
        showYear(earlier.value || 'all');
    };

    body.addEventListener('click', onClick);
    earlier?.addEventListener('change', onEarlier);
    return () => {
        body.removeEventListener('click', onClick);
        earlier?.removeEventListener('change', onEarlier);
    };
}, [page?.body]);
// Body images that fail to load: files added on the live site after the Drupal copy was
// taken are missing there, so try the live site once, then hide the image so a missing
// file does not leave an empty box. Videos (<video><source src>) missing there are
// loaded from the live site the same way.
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const copyFiles = configData.DRUPAL_DOMAIN_URL + '/sites/';
    const liveFiles = configData.DRUPAL_LIVE_DOMAIN_URL + '/sites/';
    const retryOrHide = (img) => {
        if (img.src.startsWith(copyFiles)) {
            img.src = liveFiles + img.src.slice(copyFiles.length);
        } else {
            img.style.display = 'none';
        }
    };
    const retryVideo = (video) => {
        const sources = [video, ...video.querySelectorAll('source')].filter((el) => el.src.startsWith(copyFiles));
        if (!sources.length) return;
        sources.forEach((el) => { el.src = liveFiles + el.src.slice(copyFiles.length); });
        video.load();
    };
    const onError = (event) => {
        const el = event.target;
        if (el.tagName === 'IMG') retryOrHide(el);
        else if (el.tagName === 'VIDEO') retryVideo(el);
        else if (el.tagName === 'SOURCE' && el.parentElement?.tagName === 'VIDEO') retryVideo(el.parentElement);
    };

    // error events do not bubble, so listen in the capture phase
    body.addEventListener('error', onError, true);
    body.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth === 0) retryOrHide(img);
    });
    body.querySelectorAll('video').forEach((video) => {
        if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) retryVideo(video);
    });
    return () => body.removeEventListener('error', onError, true);
}, [page?.body]);
  // A course body that was saved from the page editor comes back with its Drupal PHP broken up:
  // the start of the block at the top is lost and the rest of it is left as text (up to "?&gt;"),
  // and the link the block worked out is left as "&lt;?php echo $timezone_link; ?&gt;". Take the
  // text out and put the course's weekly chat link in, as the server does for an unbroken body.
  const timezoneLink = page?.course_chats?.[0]?.timezoneLink || "";
  // The "CALL TODAY" box under the payment details has the class "applytoday_callus_wrapper" on
  // the Drupal site, and a page's own styles can hide the box by it (the football agent
  // apprenticeship does). The box the server builds comes without the class, so add it.
  // Drupal files (/sites/...) are not on this server, so load them from the Drupal site:
  // pictures and scripts (src), and a page's own stylesheets and file links (href)
  // Application links typed with the live domain would leave this site, so keep them on the current one
  // Pages whose text format on the live site converts line breaks (LINE_BREAK_PAGES) get that first.
  const storedBody = page?.body || "";
  const bodyHtml = (LINE_BREAK_PAGES.has(Number(page?.nid)) ? drupalAutop(storedBody) : storedBody)
    .replace(/^[^<]*\?&gt;\s*/, "")
    .replace(/&lt;\?php\s+echo\s+\$timezone_link\w*\s*;?\s*\?&gt;/g, timezoneLink)
    .replace(/<div id="application_info"(?![^>]*\bclass=)(?=[^>]*>\s*<div[^>]*>\s*CALL TODAY)/g, '<div id="application_info" class="applytoday_callus_wrapper"')
    .replace(/(\s(?:src|href)=["'])\/sites\//g, `$1${configData.DRUPAL_DOMAIN_URL}/sites/`)
    .replace(/(\shref=["'])https?:\/\/(?:www\.|team\.)?sportsmanagementworldwide\.com\/application(?=["'?#])/gi, '$1/application');
  return (
    <div className="courses-overview-container">
      <div  ref={bodyRef}
                dangerouslySetInnerHTML={{
                    __html: bodyHtml
                }}
            />
      <BeTheFirstToKnowPopup open={showBeTheFirstToKnow} onClose={() => setShowBeTheFirstToKnow(false)} />
      <DocTalkPopup popup={docTalkPopup} onClose={() => setDocTalkPopup(null)} />
    </div>
  );
};
export default Body;